import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Plus, Trash2, Receipt, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, num, parseNum } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button, Field, Input } from "@/components/kit";
import { ReducePanel } from "@/components/conference/ReducePanel";

const today = () => new Date().toLocaleDateString("en-CA");

export const ENTRY_KINDS = {
  purchases: {
    field: "purchases_qty", list: "purchase_entries", title: "Compras", total: "Total comprado no período", add: "Adicionar compra",
    added: "Compra adicionada", empty: "Nenhuma compra lançada para este item.", draftField: "Compras", withDate: true,
    notePh: "Ex.: reposição de quarta, fornecedor X",
  },
  counts: {
    field: "final_qty", list: "count_entries", title: "Contagens", total: "Contagem final (soma)", add: "Adicionar contagem",
    added: "Contagem adicionada", empty: "Nenhuma contagem parcial lançada. Cada pessoa pode somar a quantidade que contou.",
    draftField: "Final", withDate: false, notePh: "Ex.: câmara fria, bar, prateleira 2",
  },
};

function EntryRow({ e, unit, canDelete, onDelete, busy, showDate, canReduce, onReduce, reducing }) {
  const [open, setOpen] = useState(false);
  const when = showDate ? fmtDate(`${e.date}T12:00:00`) : fmtDateTime(e.created_at);
  return (
    <div className="py-2.5" data-testid={`entry-${e.id}`}>
      <div className="flex items-center gap-3">
        {e.invoice_id && <Receipt size={15} className="muted shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="label tnum">+{num(e.qty)} {unit} <span className="caption">· {e.user_name}</span></div>
          <div className="caption truncate">{when}{e.note ? ` · ${e.note}` : ""}</div>
        </div>
        {canReduce && !open && <Button variant="ghost" size="sm" icon={MinusCircle} onClick={() => setOpen(true)} data-testid={`entry-reduce-${e.id}`}>Remover</Button>}
        {canDelete && !canReduce && (
          <button className="icon-btn" onClick={() => onDelete(e.id)} disabled={busy} data-testid={`entry-delete-${e.id}`}><Trash2 size={15} /></button>
        )}
      </div>
      {open && <ReducePanel max={e.qty} unit={unit} fromInvoice={!!e.invoice_id} loading={reducing} testid={`entry-reduce-panel-${e.id}`}
        onCancel={() => setOpen(false)} onConfirm={(qty, reason) => onReduce(e.id, qty, reason, () => setOpen(false))} />}
    </div>
  );
}

function EntryForm({ cfg, unit, onAdd, loading }) {
  const [f, setF] = useState({ qty: "", date: today(), note: "" });
  const submit = () => parseNum(f.qty) > 0 && onAdd(f, () => setF({ ...f, qty: "", note: "" }));
  return (
    <div className="flex flex-col gap-3 border-t pt-4" style={{ borderColor: "var(--border)" }}>
      <div className={`grid gap-3 ${cfg.withDate ? "grid-cols-2" : "grid-cols-1"}`}>
        <Field label={`Quantidade (${unit})`}><Input inputMode="decimal" autoFocus placeholder="0" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="entry-qty-input" /></Field>
        {cfg.withDate && <Field label="Data"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="entry-date-input" /></Field>}
      </div>
      <Field label="Observação (opcional)"><Input maxLength={120} placeholder={cfg.notePh} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="entry-note-input" /></Field>
      <Button icon={Plus} disabled={parseNum(f.qty) <= 0} loading={loading} onClick={submit} data-testid="entry-add-button">{cfg.add}</Button>
    </div>
  );
}

export default function EntriesDialog({ kind, open, onOpenChange, confId, it, editable, onUpdated, hasDraft }) {
  const cfg = ENTRY_KINDS[kind];
  const { user, isMgmt } = useAuth();
  const entries = it[cfg.list] || [];
  const base = Math.max(0, Math.round(((it[cfg.field] || 0) - entries.reduce((a, e) => a + e.qty, 0)) * 1000) / 1000);
  const url = `/conferences/${confId}/items/${it.product_id}/${kind}`;
  const add = useMutation({
    mutationFn: (f) => api(url, { method: "POST", body: { qty: parseNum(f.qty), date: cfg.withDate ? f.date : null, note: f.note } }),
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api(`${url}/${id}`, { method: "DELETE" }),
    onSuccess: (d) => { onUpdated(d); toast.success("Lançamento removido"); },
    onError: (e) => toast.error(e.message),
  });
  const onAdd = (f, reset) => add.mutate(f, { onSuccess: (d) => { onUpdated(d); reset(); toast.success(cfg.added); } });
  const reduce = useMutation({
    mutationFn: ({ id, qty, reason }) => api(`${url}/${id}/reduce`, { method: "POST", body: { qty, reason } }),
    onError: (e) => toast.error(e.message),
  });
  const onReduce = (id, qty, reason, done) => reduce.mutate({ id, qty, reason }, { onSuccess: (d) => { onUpdated(d); done(); toast.success("Quantidade removida da compra"); } });
  const canReduce = editable && kind === "purchases" && isMgmt;
  const canDelete = (e) => editable && !e.invoice_id && (kind === "purchases" || isMgmt || e.user_id === user?.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl" data-testid={`${kind}-dialog-${it.product_id}`}>
        <DialogTitle className="h2">{cfg.title} · {it.product_name}</DialogTitle>
        <div className="rounded-xl p-3 flex justify-between items-center" style={{ background: "var(--brand-tint)" }}>
          <span className="label">{cfg.total}</span>
          <span className="h3 tnum" data-testid={`${kind}-dialog-total`}>{num(it[cfg.field])} {it.unit}</span>
        </div>
        {editable && hasDraft && (
          <p className="caption rounded-lg p-2.5" style={{ background: "var(--accent-soft)", color: "var(--text)" }} data-testid="entry-draft-warning">
            O valor digitado no campo {cfg.draftField} ainda não foi salvo. Ao adicionar ou remover um lançamento, ele será substituído pelo total salvo.
          </p>
        )}
        <div className="divide">
          {base > 0 && <div className="flex items-center gap-3 py-2.5 caption"><span className="flex-1">Valor digitado direto no campo</span><span className="tnum">{num(base)} {it.unit}</span></div>}
          {entries.map((e) => <EntryRow key={e.id} e={e} unit={it.unit} showDate={cfg.withDate} canDelete={canDelete(e)} onDelete={(id) => del.mutate(id)} busy={del.isPending} canReduce={canReduce} onReduce={onReduce} reducing={reduce.isPending} />)}
          {!entries.length && !base && <p className="caption py-3">{cfg.empty}</p>}
        </div>
        {editable && <EntryForm cfg={cfg} unit={it.unit} onAdd={onAdd} loading={add.isPending} />}
      </DialogContent>
    </Dialog>
  );
}
